import {type FC, useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Form, Row, Col, Radio, Input, Space, Button} from 'antd';
import Loading from '@app/components/Loading/Loading';
import {useIsDemo} from '@app/hooks/useIsDemo';
import {EPaymentInterval} from '@app/constants';
import {
  useCreatePaymentCardMutation,
  useUpdatePaymentCardMutation,
  useGetPaymentCardQuery,
} from '@app/redux/services/paymentService';
import {
  useGetMeQuery,
  useLazyGetMeQuery,
} from '@app/redux/services/userService';
import {useAppDispatch} from '@app/redux/reduxHooks';
import {updateUserInfo} from '@app/redux/slices/userSlice';
import showNotification from '@app/utils/showNotification';
import {useNavigate} from 'react-router-dom';
import {Routes} from '@app/router/Routes';
import {LogEvents, useSendEvent} from '@app/utils/logger';
import type {TError} from '@app/types';
import PayjpCheckout from './PayjpCheckout';

const PaymentCardForm: FC = () => {
  const {t} = useTranslation();
  const sendEvent = useSendEvent();
  const isDemo = useIsDemo();
  const navigate = useNavigate();

  const [interval, setInterval] = useState<EPaymentInterval>(
    EPaymentInterval.monthly,
  );

  const dispatch = useAppDispatch();
  const [getUserMe] = useLazyGetMeQuery();
  const {data: me, isLoading: isGettingMe} = useGetMeQuery();
  const {data: card, isLoading: isGettingCard} = useGetPaymentCardQuery();
  const [createPaymentCard, {isLoading: isCreatingCard}] =
    useCreatePaymentCardMutation();
  const [updatePaymentCard, {isLoading: isUpdatingCard}] =
    useUpdatePaymentCardMutation();

  const isLoading =
    isCreatingCard || isUpdatingCard || isGettingCard || isGettingMe;

  useEffect(() => {
    if (me && me.payment) setInterval(me.payment.interval);
  }, [me]);

  const saveCard = async (verifiedToken: string) => {
    try {
      if (card) {
        sendEvent(LogEvents.PAYMENT_EDIT_PRESS, {cardId: card.id});
        await updatePaymentCard({token: verifiedToken}).unwrap();
      } else {
        sendEvent(LogEvents.PAYMENT_REGISTER_PRESS);
        await createPaymentCard({token: verifiedToken, interval}).unwrap();
        const user = await getUserMe().unwrap();
        dispatch(updateUserInfo(user));
      }

      const successMessage = card
        ? t('Successfully updated!')
        : t('Successfully registered!');
      showNotification(t('Success'), successMessage, 'bottomRight', 'success');
    } catch (error) {
      console.error(error);
      const {data: errorData} = error as TError;
      if (errorData) {
        sendEvent(
          card
            ? LogEvents.ERROR_PAYMENT_EDIT
            : LogEvents.ERROR_PAYMENT_REGISTER,
          card ? {cardId: card.id, ...errorData} : {...errorData},
        );
      }
      const errorMessage = card
        ? t('An error occurred during the update!')
        : t('An error occurred during the registration!');
      showNotification(t('Error'), errorMessage, 'bottomRight', 'error');
    }
  };

  const onUnsubscribe = () => {
    sendEvent(LogEvents.PAYMENT_UNSUBSCRIBE_BUTTON_PRESS);
    navigate(Routes.Unsubscribe);
  };

  const onCreated = useCallback((token: string) => {
    saveCard(token);
  }, []);

  const onFailed = useCallback((statusCode: number, message: string) => {
    console.error(statusCode, message);
  }, []);

  return (
    <div>
      {isLoading && <Loading />}
      <Form layout="vertical">
        <Row gutter={16} align="middle">
          <Col xs={24} sm={24} md={24} lg={16} xl={16}>
            <Row gutter={16} align="top">
              <Col xs={24} sm={24} md={24} lg={8} xl={8}>
                <Form.Item label={t('Card number')}>
                  <Input
                    disabled={true}
                    placeholder="**** **** **** ****"
                    value={card ? `**** **** **** ${card.last4}` : undefined}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={8} xl={8}>
                <Form.Item label={t('Expiration date(MM/YY)')}>
                  <Input
                    disabled={true}
                    placeholder="**/**"
                    value={
                      card
                        ? `${card.exp_month.toString().padStart(2, '0')}/${card.exp_year.toString().slice(2, 4)}`
                        : undefined
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={8} xl={8}>
                <Form.Item label={t('Full name')}>
                  <Input
                    disabled={true}
                    placeholder={t('Placeholders.Full name')}
                    value={card?.name}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col xs={24} sm={24} md={24} lg={8} xl={8}>
            <Form.Item label={t('Payment Count')}>
              <Radio.Group
                buttonStyle="solid"
                disabled={!isDemo}
                value={interval}
                onChange={(e) => setInterval(e.target.value)}
              >
                <Radio.Button value={EPaymentInterval.monthly}>
                  {t('Pay MONTHLY')}
                </Radio.Button>
                <Radio.Button value={EPaymentInterval.annual}>
                  {t('Pay ANNUAL')}
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Space>
        <PayjpCheckout
          text={card ? 'カード情報を更新' : 'カード情報を登録'}
          submitText={t('Save')}
          onFailed={onFailed}
          onCreated={onCreated}
          namePlaceholder={t('Placeholders.Full name')}
        />
        {!isDemo && (
          <Button danger onClick={onUnsubscribe}>
            {t('Cancellation request')}
          </Button>
        )}
      </Space>
    </div>
  );
};

export default PaymentCardForm;
